import PermissionButton from "../../components/ui/PermissionButton";
import {
  X,
  Receipt,
  Calendar,
  CreditCard,
  Wallet,
  FileText,
  Paperclip,
} from "lucide-react";

export default function IncomeDetailsModal({ income, onClose }) {
  if (!income) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-5xl rounded-2xl bg-[#0b0b0f] border border-white/10 overflow-hidden">
        {/* Header */}

        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">Income Details</h2>

            <p className="text-sm text-gray-400 mt-1">
              Voucher • {income.income_no}
            </p>
          </div>

          <PermissionButton permissionModule="income"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </PermissionButton>
        </div>

        <div className="p-8 space-y-8">
          {/* Amount */}

          <div className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-green-500/5 border border-emerald-500/20 p-6">
            <p className="text-xs uppercase tracking-widest text-gray-400">
              Income Amount
            </p>

            <h2 className="text-4xl font-serif text-emerald-400 mt-3">
              ₹{Number(income.amount || 0).toLocaleString("en-IN")}
            </h2>
          </div>

          {/* Information */}

          <div className="grid md:grid-cols-2 gap-6">
            <InfoCard
              icon={Receipt}
              title="Income Number"
              value={income.income_no}
            />

            <InfoCard
              icon={Calendar}
              title="Income Date"
              value={income.income_date}
            />

            <InfoCard
              icon={Wallet}
              title="Income Account"
              value={income.account_name}
            />

            <InfoCard icon={FileText} title="Source" value={income.source} />

            <InfoCard
              icon={CreditCard}
              title="Payment Mode"
              value={income.payment_mode}
            />

            <InfoCard
              icon={Receipt}
              title="Reference"
              value={income.reference || "-"}
            />
          </div>

          {/* Notes */}

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Notes</h3>

            <p className="text-gray-300 leading-7">
              {income.notes || "No notes available."}
            </p>
          </div>

          {/* Attachment */}

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Attachment</h3>

            <div className="flex items-center gap-3">
              <Paperclip size={18} className="text-amber-400" />

              {income.attachment ? (
                <a
                  href={income.attachment}
                  target="_blank"
                  rel="noreferrer"
                  className="text-amber-400 hover:underline"
                >
                  View Attachment
                </a>
              ) : (
                <span className="text-gray-400">No attachment uploaded</span>
              )}
            </div>
          </div>

          {/* Metadata */}

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Record Information</h3>

            <Row label="Created At" value={income.created_at || "-"} />

            <Row
              label="Created By"
              value={income.created_by_name || income.created_by || "-"}
            />
          </div>
        </div>

        {/* Footer */}

        <div className="flex justify-end gap-4 px-8 py-5 border-t border-white/10">
          <PermissionButton permissionModule="income"
            onClick={onClose}
            className="px-6 py-2 rounded-xl bg-amber-400 text-black font-semibold"
          >
            Close
          </PermissionButton>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-5 flex gap-4">
      <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
        <Icon size={20} className="text-amber-400" />
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </p>

        <p className="mt-2 font-medium text-white">{value || "-"}</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-white/10 last:border-0">
      <span className="text-gray-400">{label}</span>

      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}
